/**
 * Symbol Picker Component
 *
 * Compact panel with symbols and emojis that can be inserted into plate text.
 * Uses accordion-style sections to save space.
 */

import { useState } from 'react'

interface SymbolPickerProps {
  onSymbolClick: (symbol: string) => void
}

const EMOJIS = ['❤️', '⭐', '🚗', '⚡', '🔥', '😎', '🎁', '💍', '👑', '🎉', '💖', '🌟', '🏆', '💎', '🎯']
const SYMBOLS = ['•', '★', '☆', '♥', '♛', '✿', '❀', '✧', '✦', '✩', '✪', '✫', '✬', '✭', '✮']

const SymbolPicker = ({ onSymbolClick }: SymbolPickerProps) => {
  const [openSection, setOpenSection] = useState<'emojis' | 'symbols' | null>('emojis')

  const toggleSection = (section: 'emojis' | 'symbols') => {
    setOpenSection(prev => prev === section ? null : section)
  }

  return (
    <div className="bg-dark-800/50 rounded-xl border border-gray-800/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800/50">
        <h4 className="text-sm font-semibold text-gray-200">Symbole & Emojis</h4>
      </div>

      {/* Emojis Section */}
      <div>
        <button
          onClick={() => toggleSection('emojis')}
          className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
        >
          <span className="text-xs font-medium text-gray-400">Emojis</span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${openSection === 'emojis' ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {openSection === 'emojis' && (
          <div className="px-3 pb-3">
            <div className="grid grid-cols-5 gap-1.5">
              {EMOJIS.map((emoji, i) => (
                <button
                  key={i}
                  onClick={() => onSymbolClick(emoji)}
                  className="p-2 bg-dark-700/50 hover:bg-dark-600 rounded-lg text-xl transition-all hover:scale-110 active:scale-95"
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Symbols Section */}
      <div className="border-t border-gray-800/30">
        <button
          onClick={() => toggleSection('symbols')}
          className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-dark-700/30 transition-colors"
        >
          <span className="text-xs font-medium text-gray-400">Symbole</span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${openSection === 'symbols' ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {openSection === 'symbols' && (
          <div className="px-3 pb-3">
            <div className="grid grid-cols-5 gap-1.5">
              {SYMBOLS.map((symbol, i) => (
                <button
                  key={i}
                  onClick={() => onSymbolClick(symbol)}
                  className="p-2 bg-dark-700/50 hover:bg-dark-600 rounded-lg text-lg font-bold transition-all hover:scale-110 active:scale-95"
                  style={{ color: '#FFD700' }}
                  title={symbol}
                >
                  {symbol}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SymbolPicker
